import React, { createContext, useContext, useEffect, useState } from 'react';
import { getEnv } from 'shared/get-env';
import { io, Socket } from 'socket.io-client';

import { useAuth } from './useAuth';

interface WebSocketContextType {
  isOnline: boolean;
  socket: Socket | null;
  attempts: number;
}

type SocketAuthenticatedUser = {
  access_token?: string;
  accessToken?: string;
  token?: string;
};

const WebSocketContext = createContext<WebSocketContextType>({
  isOnline: false,
  socket: null,
  attempts: 0,
});

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { auth, currentUser } = useAuth();

  const [attempts, setAttempts] = useState(0);
  const [isOnline, setIsOnline] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);

  const maxAttempts = 10;
  const url = getEnv('REACT_APP_API_URL');

  useEffect(() => {
    if (!auth?.activeProfile) return;
    if (!url) return;

    const authenticatedUser = currentUser as
      | SocketAuthenticatedUser
      | undefined;
    let currentSocket: Socket | null = null;
    let isUnmounted = false;
    let currentAttempts = 1;

    const connect = () => {
      if (currentAttempts > maxAttempts) return;
      setAttempts(currentAttempts);

      currentSocket = io(url + '/director', {
        autoConnect: true,
        path: '/web/realtime/socket.io',
        reconnection: true,
        withCredentials: true,
      });
      setSocket(currentSocket);

      currentSocket.emit('authenticate', {
        token:
          authenticatedUser?.access_token ||
          authenticatedUser?.accessToken ||
          authenticatedUser?.token,
      });

      currentSocket.on('authenticated', () => {
        setIsOnline(true);
      });

      currentSocket.on('unauthorized', () => {
        setIsOnline(false);
        currentSocket?.disconnect();
      });

      currentSocket.on('disconnect', () => {
        if (isUnmounted) return;
        setIsOnline(false);
        if (currentAttempts < maxAttempts) {
          setTimeout(() => {
            currentAttempts += 1;
            connect();
          }, 2000);
        }
      });

      currentSocket.on('connect_error', () => {
        if (isUnmounted) return;
        setIsOnline(false);
        currentSocket?.disconnect();
      });
    };

    connect();

    return () => {
      isUnmounted = true;
      currentSocket?.disconnect();
    };
  }, [auth, currentUser, url]);

  return (
    <WebSocketContext.Provider value={{ isOnline, socket, attempts }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => useContext(WebSocketContext);
